import React, { useContext, useState } from 'react';
import { Product } from '../../../../model/product';
import { Card } from '../../../../components/Card/Card';
import { t } from '../../../../locales/translate';
import { KeyValueTable } from '../../../../components/KeyValueTable/KeyValueTable';
import { formatDateTime } from '../../../../util/util';
import { PermissionButton } from '../../../../components/Button/PermissionButton';
import { ControlledModal } from '../../../../components/Modal/ControlledModal';
import { MusicChartsForm } from '../Modals/MusicChartsForm';
import { FileUploadModal } from '../../../../components/file_upload_modal/file_upload_modal';
import { runMusicChartOverridesImport } from '../../../../model/imports';
import { ActivitiesContext } from '../../../../components/Activities/Activities';
import { Icon } from '../../../../components/Icon/Icon';
import { useTask } from '../../../../model/data';
import { runChartOverridesExport } from '../../../../model/exports';
import { useModalPresenter } from '../../../../components/Modal/PresentedModal';
import { handleSuccess } from '../../../../model/notification';

interface Props {
  product: Product;
}

export function MusicCharts({ product }: Props): JSX.Element {
  const { reloadActivities } = useContext(ActivitiesContext);
  const [displayEditModal, setDisplayEditModal] = useState(false);
  const uploadChartsOverridesModal = useModalPresenter();
  const exportTask = useTask({ handleError: true });

  return (
    <Card
      title={t.model.musicCharts._name}
      actions={
        <>
          <PermissionButton
            name="plus"
            model={product}
            permission="update_chart_overrides"
            onClick={() => {
              uploadChartsOverridesModal.present(FileUploadModal, {
                title: t.model.musicCharts.uploadOverrides,
                submit: (file) => runMusicChartOverridesImport(product.id, file),
                onUploadComplete: () => {
                  reloadActivities();
                  handleSuccess({ title: t.model.charts.uploadSuccess, message: t.model.charts.uploadSuccessful });
                },
              });
            }}
            tooltip={t.model.musicCharts.uploadOverrides}
          />
          <Icon
            name="download"
            onClick={() => exportTask.run(runChartOverridesExport({ productId: product.id }))}
            tooltip={t.model.musicCharts.downloadChartOverrides}
          />
          <PermissionButton
            name="edit"
            model={product}
            permission="isAdmin"
            onClick={() => setDisplayEditModal(true)}
          />
        </>
      }
    >
      <KeyValueTable
        displayEmpty
        emptyMsg={t.model.product.noMusicChart}
        labelWidth={200}
        items={[
          {
            key: t.model.musicCharts.recordLabel,
            value: product.recordLabel,
          },
          {
            key: t.model.musicCharts.recordDistributor,
            value: product.recordDistributor,
          },
          {
            key: t.model.musicCharts.recordReleasedAt,
            value: formatDateTime(product.recordReleasedAt),
          },
          {
            key: t.model.musicCharts.recordType,
            value: product.recordType,
          },
        ]}
      ></KeyValueTable>
      <ControlledModal<Product>
        open={displayEditModal}
        model={product}
        onClose={() => setDisplayEditModal(false)}
        size="small"
        title={t.model.musicCharts.editMusicCharts}
      >
        {(controller) => <MusicChartsForm controller={controller} />}
      </ControlledModal>
    </Card>
  );
}
